import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSelectModule } from '@angular/material/select';
import { UserSettings } from '../../models/user-settings';
import { LoadingService } from '../../services/loading.service';
import { ToastService } from '../../services/toast.service';

// Type definition for settings values
export type SettingValueType = 'string' | 'number' | 'boolean' | 'object' | 'array' | 'null' | 'undefined';

// Interface for setting metadata
export interface SettingMetadata {
  key: string;
  type: SettingValueType;
  isComplex: boolean;
  stringValue: string;
}

// Interface for new setting
export interface NewSetting {
  key: string;
  value: string;
  type: SettingValueType;
}

@Component({
  selector: 'app-user-settings-editor',
  templateUrl: './user-settings-editor.component.html',
  styleUrls: ['./user-settings-editor.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatSlideToggleModule,
    MatSelectModule
  ]
})
export class UserSettingsEditorComponent implements OnInit, OnChanges {
  // Input from parent component
  @Input() userSettings: UserSettings | null = null;
  @Input() saveSuccess = false;
  @Input() saveError = false;

  // Events to emit to parent
  @Output() saveSettings = new EventEmitter<{[key: string]: any}>();
  @Output() addSetting = new EventEmitter<NewSetting>();
  @Output() removeSetting = new EventEmitter<string>();

  // Component state
  settingsForm: FormGroup;
  settingsMetadata: SettingMetadata[] = [];
  jsonEditMode: { [key: string]: boolean } = {};

  // New setting values
  newSettingKey = '';
  newSettingValue = '';
  newSettingType: SettingValueType = 'string';

  // UI helpers
  get objectSample(): string { return '{"key": "value"}'; }
  get arraySample(): string { return '["item1", "item2"]'; }
  get lBrace(): string { return '{'; }
  get rBrace(): string { return '}'; }

  // Available setting types for new settings
  availableTypes: { value: SettingValueType, label: string }[] = [
    { value: 'string', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'boolean', label: 'True/False' },
    { value: 'object', label: 'Object' },
    { value: 'array', label: 'Array' }
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly toast: ToastService,
    public readonly loadingService: LoadingService
  ) {
    // Initialize an empty form
    this.settingsForm = this.fb.group({
      settings: this.fb.group({})
    });
  }

  ngOnInit(): void {
    if (this.userSettings) {
      this.initSettingsForm(this.userSettings);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When settings change from the parent, reinitialize the form
    if (changes['userSettings'] && changes['userSettings'].currentValue) {
      this.initSettingsForm(changes['userSettings'].currentValue);
    }
  }

  // Get the type of a value
  getValueType(value: any): SettingValueType {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';

    const type = typeof value;

    if (type === 'object') {
      if (Array.isArray(value)) return 'array';
      return 'object';
    }

    if (type === 'string' || type === 'number' || type === 'boolean') {
      return type as SettingValueType;
    }

    return 'string'; // default fallback
  }

  // Check if a value is complex (object or array)
  isComplexValue(value: any): boolean {
    const type = this.getValueType(value);
    return type === 'object' || type === 'array';
  }

  // Convert a value to a string representation
  valueToString(value: any): string {
    if (value === null) return 'null';
    if (value === undefined) return 'undefined';

    const type = this.getValueType(value);

    if (type === 'object' || type === 'array') {
      try {
        return JSON.stringify(value, null, 2);
      } catch (e) {
        return String(value);
      }
    }

    return String(value);
  }

  // Parse a string value to its appropriate type
  parseStringValue(value: string, type: SettingValueType): any {
    switch (type) {
      case 'number':
        return Number(value);
      case 'boolean':
        return value.toLowerCase() === 'true';
      case 'object':
      case 'array':
        try {
          return JSON.parse(value);
        } catch (e) {
          this.toast.error('Invalid JSON format');
          return type === 'array' ? [] : {};
        }
      case 'null':
        return null;
      case 'undefined':
        return undefined;
      case 'string':
      default:
        return value;
    }
  }

  private initSettingsForm(settings: UserSettings): void {
    // Convert settings to a form
    const settingsGroup = this.fb.group({});
    this.settingsMetadata = [];

    // Add each setting as a form control with metadata
    Object.entries(settings.settings).forEach(([key, value]) => {
      const type = this.getValueType(value);
      const isComplex = this.isComplexValue(value);
      const stringValue = this.valueToString(value);

      // Add to form - we store all values as strings initially
      settingsGroup.addControl(key, this.fb.control(stringValue));

      // Add metadata
      this.settingsMetadata.push({
        key,
        type,
        isComplex,
        stringValue
      });

      // Initialize edit mode status
      this.jsonEditMode[key] = false;
    });

    // Update the form
    this.settingsForm = this.fb.group({
      settings: settingsGroup
    });
  }

  // Toggle JSON edit mode for complex values
  toggleJsonEditMode(key: string): void {
    this.jsonEditMode[key] = !this.jsonEditMode[key];
  }

  // Get the form control for a specific key
  getSettingControl(key: string): any {
    return this.settingsForm.get('settings')?.get(key);
  }

  // Get metadata for a specific key
  getSettingMetadata(key: string): SettingMetadata | undefined {
    return this.settingsMetadata.find(meta => meta.key === key);
  }

  // Format JSON string for display
  formatJson(value: string): string {
    try {
      const obj = JSON.parse(value);
      return JSON.stringify(obj, null, 2);
    } catch (e) {
      return value;
    }
  }

  // Update the type of a setting
  updateSettingType(key: string, newType: SettingValueType): void {
    const metadata = this.getSettingMetadata(key);
    if (!metadata) return;

    const control = this.getSettingControl(key);
    if (!control) return;

    // Set appropriate default value based on the new type
    let newValue: string;

    switch (newType) {
      case 'string':
        if (metadata.isComplex) {
          // If converting from a complex type, keep the string representation
          newValue = control.value;
        } else {
          // Otherwise just convert to string
          newValue = String(this.parseStringValue(control.value, metadata.type));
        }
        break;
      case 'number':
        newValue = '0'; // Default numeric value
        break;
      case 'boolean':
        newValue = 'false'; // Default boolean value
        break;
      case 'object':
        newValue = '{}'; // Default empty object
        break;
      case 'array':
        newValue = '[]'; // Default empty array
        break;
      default:
        newValue = ''; // Default for any other types including 'null'
    }

    // Update form control
    control.setValue(newValue);

    // Update metadata
    metadata.type = newType;
    metadata.isComplex = (newType === 'object' || newType === 'array');
    metadata.stringValue = newValue;
  }

  // Save all settings
  onSaveSettings(): void {
    if (!this.settingsForm.valid) return;

    const formValue = this.settingsForm.get('settings')?.value;
    if (!formValue) return;

    // Convert all values from strings to their appropriate types
    const parsedSettings: {[key: string]: any} = {};
    Object.entries(formValue).forEach(([key, value]) => {
      const metadata = this.getSettingMetadata(key);
      if (metadata) {
        parsedSettings[key] = this.parseStringValue(value as string, metadata.type);
      }
    });

    this.saveSettings.emit(parsedSettings);
  }

  // Add a new setting
  onAddSetting(): void {
    if (!this.newSettingKey || !this.newSettingValue) {
      this.toast.error('Please provide both key and value');
      return;
    }

    // Create the new setting with correct type
    const newSetting: NewSetting = {
      key: this.newSettingKey,
      value: this.newSettingValue,
      type: this.newSettingType
    };

    // Emit event to parent component
    this.addSetting.emit(newSetting);

    // Reset form
    this.newSettingKey = '';
    this.newSettingValue = '';
    this.newSettingType = 'string';
  }

  // Remove a setting
  onRemoveSetting(key: string): void {
    this.removeSetting.emit(key);
  }

  // Get default value placeholder based on type
  getDefaultValueForType(type: SettingValueType): string {
    switch (type) {
      case 'string':
        return '';
      case 'number':
        return '0';
      case 'boolean':
        return 'false';
      case 'object':
        return '{}';
      case 'array':
        return '[]';
      default:
        return '';
    }
  }

  // Get all setting keys
  get settingsKeys(): string[] {
    return this.settingsMetadata.map(meta => meta.key);
  }
}
